import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { useListCreditHistory } from "@/hooks/useBilling"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination"
import { Calendar, FileText } from "lucide-react"
import { formatNumberWithCommas } from "@/lib/utils/characters"

const ITEMS_PER_PAGE = 5

export default function CreditsHistory() {
  const [currentPage, setCurrentPage] = useState(1)
  const offset = (currentPage - 1) * ITEMS_PER_PAGE

  const { data: creditHistory, isLoading } = useListCreditHistory(ITEMS_PER_PAGE, offset)

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice history</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Invoices are issued when credits are purchased. All dates are in GMT+1 timezone.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                <span className="text-sm text-gray-500">Loading invoice history...</span>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Invoice Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {creditHistory && creditHistory.credit_history.length > 0 ? (
                    creditHistory.credit_history.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {new Date(item.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2 capitalize">
                            <FileText className="h-4 w-4 text-gray-400" />
                            {item.transaction_type}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {formatNumberWithCommas(item.current_credits - item.previous_credits)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No invoice history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Footer with record count and pagination */}
        {creditHistory && (
          <div className="flex justify-end border-t border-gray-200 px-4 py-2 dark:border-gray-700">
            <div className="flex items-center gap-4">
              <p className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                Showing {creditHistory.offset + 1}-
                {Math.min(creditHistory.offset + ITEMS_PER_PAGE, creditHistory.total)} of {creditHistory.total}
              </p>

              {creditHistory.credit_history.length > 0 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>

                    {/* Page numbers with smart pagination */}
                    {(() => {
                      const totalPages = Math.ceil(creditHistory.total / ITEMS_PER_PAGE) || 1
                      const pages = []

                      // Always show first page
                      pages.push(
                        <PaginationItem key={1}>
                          <PaginationLink
                            onClick={() => setCurrentPage(1)}
                            isActive={currentPage === 1}
                            className="cursor-pointer"
                          >
                            1
                          </PaginationLink>
                        </PaginationItem>
                      )

                      // Show ellipsis if current page is far from start
                      if (currentPage > 4) {
                        pages.push(
                          <PaginationItem key="ellipsis-start">
                            <PaginationEllipsis />
                          </PaginationItem>
                        )
                      }

                      // Show pages around current page
                      const startPage = Math.max(2, currentPage - 1)
                      const endPage = Math.min(totalPages - 1, currentPage + 1)

                      for (let page = startPage; page <= endPage; page++) {
                        if (page !== 1 && page !== totalPages) {
                          pages.push(
                            <PaginationItem key={page}>
                              <PaginationLink
                                onClick={() => setCurrentPage(page)}
                                isActive={currentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        }
                      }

                      // Show ellipsis if current page is far from end
                      if (currentPage < totalPages - 3) {
                        pages.push(
                          <PaginationItem key="ellipsis-end">
                            <PaginationEllipsis />
                          </PaginationItem>
                        )
                      }

                      // Always show last page (if more than 1 page)
                      if (totalPages > 1) {
                        pages.push(
                          <PaginationItem key={totalPages}>
                            <PaginationLink
                              onClick={() => setCurrentPage(totalPages)}
                              isActive={currentPage === totalPages}
                              className="cursor-pointer"
                            >
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      }

                      return pages
                    })()}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        className={
                          currentPage >= Math.ceil(creditHistory.total / ITEMS_PER_PAGE)
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
